// EditPromptPage.js
import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    CircularProgress,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { GetallPrompts, UpdatePrompt } from '../../utils/templatesApi';

function EditPromptPage() {
    const [prompts, setPrompts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [newText, setNewText] = useState('');

    // Fetch prompts from backend
    useEffect(() => {
        const fetchPrompts = async () => {
            setLoading(true);
            try {
                const response = await GetallPrompts();
                setPrompts(response || []);
            } catch (err) {
                console.error(err);
                setError('Failed to fetch prompts');
            } finally {
                setLoading(false);
            }
        };
        fetchPrompts();
    }, []);

    const openModal = (prompt) => {
        setEditingPrompt(prompt);
        setNewText(prompt.prompt);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingPrompt(null);
        setNewText('');
    };

    const saveChanges = async () => {
        if (!newText.trim()) {
            alert('Prompt cannot be empty');
            return;
        }
        if (!editingPrompt?.id) {
            alert('Cannot save: missing prompt ID');
            return;
        }
        try {
            const updatedPrompt = await UpdatePrompt(editingPrompt.id, newText);
            setPrompts((prev) =>
                prev.map((p) =>
                    p.id === editingPrompt.id ? { ...p, prompt: updatedPrompt.prompt } : p
                )
            );
            closeModal();
        } catch (err) {
            console.error(err);
            alert('Error saving changes');
        }
    };

    if (loading)
        return (
            <div style={{ textAlign: 'center', marginTop: 50 }}>
                <CircularProgress />
            </div>
        );
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <div style={{ padding: 20 }}>
            <Typography variant="h5" gutterBottom>
                All Prompts
            </Typography>

            <TableContainer component={Paper} elevation={3}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Prompt Preview</TableCell>
                            <TableCell align="center">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prompts.map((p, index) => (
                            <TableRow key={p.id}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{p.email}</TableCell>
                                <TableCell style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {p.prompt.length > 500 ? p.prompt.substring(0, 500) + '...' : p.prompt}
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Edit Prompt">
                                        <IconButton color="primary" onClick={() => openModal(p)}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Edit Modal */}
            <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="md">
                <DialogTitle>Edit Prompt</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle1" gutterBottom>
                        MailBox: <strong>{editingPrompt?.email}</strong>
                    </Typography>
                    <TextField
                        value={newText}
                        onChange={(e) => setNewText(e.target.value)}
                        multiline
                        minRows={8}
                        maxRows={20}
                        variant="outlined"
                        fullWidth
                        style={{ fontFamily: 'monospace' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal} color="secondary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={saveChanges} color="primary" variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default EditPromptPage;